import { type ReactNode } from "react";

import { ToggleIcon } from "../icon/ToggleIcon";
import { IconButton, styled, Box } from "~/common/mui";

export interface TreeTableNode<T extends TreeTableNode<T>> {
  id: string;
  children: T[];
  expanded?: boolean;
}

export interface TreeTableColumnDef<T extends TreeTableNode<T>> {
  title: ReactNode | (() => ReactNode);
  width: string;
  headerClass?: string;
  bodyClass?: string;
  render: keyof T | ((row: T) => ReactNode);
}

interface TreeTableProps<T extends TreeTableNode<T>> {
  className?: string;
  data: TreeTableNode<T> | TreeTableNode<T>[];
  colDefs: TreeTableColumnDef<T>[];
  indentation?: number;
  onToggleExpand: (nodeId: string, newValue: boolean) => void;
}

const TableRoot = styled("div")(({ theme }) => ({
  borderBottom: `1px solid ${theme.palette.divider}`,
}));

const Header = styled("div")(({ theme }) => ({
  ...theme.typography.labelLarge,
  paddingBottom: 10,
  borderBottom: `3px solid ${theme.palette.divider}`,
  display: "flex",
}));

const HeaderCell = styled("div")({
  flex: "none",
});

const Row = styled("div")({
  display: "flex",
  width: "100%",
});

const BodyCell = styled("div")(({ theme }) => ({
  paddingTop: 10,
  paddingBottom: 10,
  position: "relative",
  flex: "none",
  "&:before, &:after": {
    content: '""',
    position: "absolute",
    left: 0,
    right: 0,
    height: 1,
    background: theme.palette.divider,
  },
  "&:before": {
    top: -1,
  },
  "&:after": {
    bottom: 0,
  },
}));

const ExpandButton = styled(IconButton)({
  margin: "-12px -4px -12px -2px",
});

export function TreeTable<T extends TreeTableNode<T>>(props: TreeTableProps<T>) {
  const indentation = props.indentation ?? 16;

  const data = Array.isArray(props.data) ? props.data : [props.data];

  return (
    <TableRoot className={props.className}>
      <TreeTableHeader {...props} />
      {data.map((node) => (
        <TTNode key={node.id} {...props} cumulativeIndentation={0} indentation={indentation} data={node} />
      ))}
    </TableRoot>
  );
}

function TreeTableHeader<T extends TreeTableNode<T>>(props: TreeTableProps<T>) {
  return (
    <Header>
      {props.colDefs.map((def, index) => {
        return (
          <HeaderCell key={index} style={{ width: def.width }}>
            {typeof def.title === "function" ? def.title() : def.title}
          </HeaderCell>
        );
      })}
    </Header>
  );
}

type TreeTableNodeProps<T extends TreeTableNode<T>> = Omit<TreeTableProps<T>, "className" | "indendation" | "data"> & {
  indentation: number;
  cumulativeIndentation: number;
  data: TreeTableNode<T>;
};

function TTNode<T extends TreeTableNode<T>>(props: TreeTableNodeProps<T>) {
  const renderCell = (def: TreeTableColumnDef<T>, data: TreeTableNode<T>) => {
    // @ts-ignore
    return typeof def.render === "function" ? def.render(data) : data[def.render];
  };

  const getCellWidth = (index: number, def: TreeTableColumnDef<T>) => {
    return index === 0 ? `calc(${def.width} - ${props.cumulativeIndentation}px` : def.width;
  };

  const showExpandButton = props.data.children.length > 0;

  return (
    <>
      <Row>
        {props.colDefs.map((def, index) => (
          <Box key={`${props.data.id}-${index}`} sx={{ display: "contents" }}>
            {index === 0 && <Box sx={{ width: `${props.cumulativeIndentation}px` }} />}
            <BodyCell style={{ width: getCellWidth(index, def) }}>
              {index === 0 && (
                <ExpandButton
                  onClick={() => props.onToggleExpand(props.data.id, !props.data.expanded)}
                  color={"primary"}
                  style={{ visibility: showExpandButton ? "visible" : "hidden" }}
                >
                  <ToggleIcon isActive={!!props.data.expanded} />
                </ExpandButton>
              )}
              {renderCell(def, props.data)}
            </BodyCell>
          </Box>
        ))}
      </Row>
      {props.data.expanded &&
        props.data.children.map((child) => (
          <TTNode
            key={child.id}
            {...props}
            data={child}
            cumulativeIndentation={props.cumulativeIndentation + props.indentation}
          />
        ))}
    </>
  );
}
